import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect, useDispatch } from "react-redux";
import AppDialog from "../../../components/Common/AppDialog";
import Notification from "../../../components/Common/Notification";
import { resetQuotationActionState, searchGeneral, updateQuotation } from "../../../store/actions";
import AddQuotation from "../AddQuotation";

const stripSingaporeCode = value => String(value || "").replace(/^\+65/, "");

const quotationToForm = quotation => {
  const pricing = quotation?.pricing || {};
  const source = quotation?.vehicle_source || "manual";
  const linkedVehicle = source === "listing"
    ? quotation?.bike_advert || quotation?.advert || quotation?.vehicle
    : quotation?.inventory || quotation?.vehicle;
  const linkedId = source === "listing" ? quotation?.bike_advert_id : quotation?.erp_id;
  const linkedIdKey = source === "listing" ? "bike_advert_id" : "erp_id";
  const bikeMakeId = quotation?.bike_make_id ?? quotation?.bike_make?.id ?? quotation?.make_id;
  return {
    vehicleSource: source,
    make: bikeMakeId ?? "",
    model: quotation?.bike_model_id ?? quotation?.bike_model?.id ?? quotation?.model_id ?? "",
    registrationNo: quotation?.register_number || "",
    bike_advert_type: quotation?.bike_advert_type || "",
    year: quotation?.manu_year || "",
    relatedLink: quotation?.related_link || linkedVehicle?.related_link || "",
    vehicle: linkedVehicle ? {
      ...linkedVehicle,
      [linkedIdKey]: linkedVehicle?.[linkedIdKey] ?? linkedId,
    } : (linkedId ? {
        id: linkedId,
        [linkedIdKey]: linkedId,
        make: quotation?.make || quotation?.bike_make?.name || "",
        model: quotation?.model || quotation?.bike_model?.name || "",
        related_link: quotation?.related_link || "",
      } : null),
    price: pricing?.machine_price ?? "",
    overtrade: pricing?.overtrade ?? "",
    coe: pricing?.coe ?? "",
    insurance: pricing?.insurance ?? "",
    // trade_in: pricing?.trade_in ?? "",
    fee: pricing?.admin_fee ?? "",
    other: pricing?.other_charges ?? "",
    downpayment: pricing?.downpayment ?? "",
    term: pricing?.loan_term ?? "",
    rate: pricing?.interest_rate ?? "",
    interest_rate_id: quotation?.interest_rate_id ?? pricing?.interest_rate_id ?? "",
    customer_name: quotation?.customer_name || "",
    customer_email: quotation?.customer_email || "",
    phone: stripSingaporeCode(quotation?.customer_phone),
    remarks: quotation?.remarks || "",
    pricing,
  };
};

const EditQuotation = ({ formObj, open, close, onSubmit, makeList, quotationUpdate, quotationError }) => {
  const dispatch = useDispatch();
  const [editAction, setEditAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({});
  const handleEditActionChange = useCallback((action) => setEditAction(action), []);
  const initialFormObj = useMemo(() => quotationToForm(formObj), [formObj]);

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setNotification({});
    dispatch(resetQuotationActionState());
    dispatch(searchGeneral({ type: "bike_makes", search: "" }));
  }, [dispatch, open, formObj?.id]);

  useEffect(() => {
    if (!submitting || quotationUpdate?.code !== "success") return;
    setSubmitting(false);
    onSubmit?.(quotationUpdate);
    dispatch(resetQuotationActionState());
  }, [dispatch, onSubmit, quotationUpdate, submitting]);

  useEffect(() => {
    if (!submitting || !quotationError || (Array.isArray(quotationError) && quotationError.length === 0)) return;
    setSubmitting(false);
    setNotification({
      open: true,
      type: "error",
      msg: quotationError?.message || "Unable to update quotation. Please try again.",
    });
  }, [quotationError, submitting]);

  const handleSubmit = useCallback((data) => {
    if (!formObj?.id || submitting) return;
    setSubmitting(true);
    dispatch(resetQuotationActionState());
    dispatch(updateQuotation({
      id: formObj.id,
      data,
    }));
  }, [dispatch, formObj, submitting]);

  const handleClose = useCallback(() => {
    if (!submitting) close?.();
  }, [close, submitting]);

  return (
    <>
      <AppDialog
        open={open}
        close={handleClose}
        title="Edit Quotation"
        width="80%"
        fullWidth
        isMobile={window.innerWidth <= 768}
        hideCloseIcon={false}
        contentSx={{
          pb: 1,
          height: "calc(100vh - 180px)",
          minHeight: 0,
        }}
        customTitle={editAction}
      >
        <AddQuotation
          key={formObj?.id}
          makeList={makeList}
          isEdit={true}
          initialFormObj={initialFormObj}
          isEditSubmitting={submitting}
          onEditSubmit={handleSubmit}
          onEditActionChange={handleEditActionChange}
          onEditClose={handleClose}
        />
      </AppDialog>
      <Notification
        notification={notification}
        setNotification={setNotification}
        errors={quotationError?.errors}
      />
    </>
  );
};

const mapStateToProps = ({ Quotation }) => ({
  quotationUpdate: Quotation.quotationUpdate,
  quotationError: Quotation.error,
});

export default connect(mapStateToProps)(EditQuotation);
